import * as z from 'zod';

const id = z.union([z.string(), z.number()]);
const finiteNumber = z
  .union([z.number(), z.string().trim().min(1)])
  .transform((value) => (typeof value === 'number' ? value : Number(value)))
  .pipe(z.number().finite());
const point = z.object({ lat: finiteNumber, long: finiteNumber });
const routePoint = z
  .object({
    location: point.nullish(),
    address: z.string().nullish(),
    arrived_at: z.string().nullish(),
    handed_over_at: z.string().nullish(),
    initial_eta: z.string().nullish(),
    eta: z.string().nullish(),
  })
  .loose();
export const trackingOrderSchema = z
  .object({
    id: id.optional(),
    display_id: id.nullish(),
    stage: finiteNumber.optional(),
    status: z.string().nullish(),
    rejectedComment: z.string().nullish(),
    origin: z.array(routePoint).nullish(),
    destination: z.array(routePoint).nullish(),
    total_delivery_price: z.union([z.number(), z.string()]).nullish(),
    created_at: z.string().nullish(),
    courier: z.object({ id: id.nullish() }).nullish(),
    origin_address: z.array(z.string()).nullish(),
    destination_address: z.array(z.string()).nullish(),
    vendor_order_id: id.nullish(),
    payment_type: z.string().nullish(),
    total_product_price: z.union([z.number(), z.string()]).nullish(),
    pricing: z
      .object({ total: finiteNumber.nullish(), products: finiteNumber.nullish() })
      .loose()
      .nullish(),
    vendor: z
      .object({ name: z.string().nullish(), is_business: z.boolean().nullish() })
      .nullish(),
    dead_head_distance: finiteNumber.nullish(),
    order_distance: finiteNumber.nullish(),
    distances: z
      .object({ dead_head: finiteNumber.nullish(), order: finiteNumber.nullish() })
      .nullish(),
  })
  .loose();
// `/couriers/in-radius` is a tracking snapshot. The map only needs usable
// route/status fields from its embedded orders, so discard malformed optional
// details rather than rejecting the entire courier list.
const embeddedTrackingOrderSchema = trackingOrderSchema.catch({});
export const trackingCourierSchema = z
  .object({
    id,
    first_name: z.string().nullish(),
    last_name: z.string().nullish(),
    phone: z.string().nullish(),
    photo: z.string().nullish(),
    profile_photo: z.string().nullish(),
    status: z.string().nullish(),
    balance: z.union([z.number(), z.string()]).nullish(),
    courier_cash: z
      .object({ cash_balance: z.union([z.number(), z.string()]).nullish() })
      .nullish(),
    location: point.nullish(),
    is_working: z.boolean().nullish(),
    is_on_break: z.boolean().nullish(),
    equipment_id: finiteNumber.nullish(),
    equipment_photo: z.string().nullish(),
    equipment_name: z.string().nullish(),
    equipment: z.object({ name_ru: z.string().nullish() }).nullish(),
    battery_percentage: z.union([z.number(), z.string()]).nullish(),
    reported_at: z.string().nullish(),
    last_reported_at: z.string().nullish(),
    last_known_grid_box: z
      .object({
        x: finiteNumber.optional(),
        y: finiteNumber.optional(),
        X: finiteNumber.optional(),
        Y: finiteNumber.optional(),
      })
      .nullish(),
    active_orders: z.array(embeddedTrackingOrderSchema).nullish(),
  })
  .loose();
export const trackingSchema = z.object({
  couriers: z.array(trackingCourierSchema).default([]),
  stats: z
    .object({
      is_working_count: z.coerce.number().optional(),
      on_break_count: z.coerce.number().optional(),
      is_not_working_count: z.coerce.number().optional(),
      free_online_count: z.coerce.number().optional(),
      has_active_order_count: z.record(z.string(), z.coerce.number()).optional(),
    })
    .default({}),
});
export const searchOrderSchema = z
  .object({
    id: id.optional(),
    display_id: id,
    origin: z.object({
      lat: finiteNumber,
      long: finiteNumber.optional(),
      lng: finiteNumber.optional(),
    }),
    vendor: z
      .object({ name: z.string().nullish(), phone: z.string().nullish() })
      .nullish(),
    created_at: z.string().nullish(),
  })
  .loose();
export const companyMarkSchema = z
  .object({
    id,
    location: point,
    name: z.string().nullish(),
    type: z.string().optional(),
  })
  .loose();
export const equipmentSchema = z.object({ id, name_ru: z.string().nullish() }).loose();
export type TrackingCourier = z.infer<typeof trackingCourierSchema>;
export type TrackingOrder = z.infer<typeof trackingOrderSchema>;
export type SearchOrder = z.infer<typeof searchOrderSchema>;
export interface TrackingFilter {
  lat: number;
  long: number;
  radius: number;
  equipment_id?: string | number | undefined;
  has_order?: boolean;
  is_working?: boolean;
  is_on_break?: boolean;
  has_order_count?: number;
  has_many_order?: boolean;
}

const toggle = (fallback = false) =>
  z.preprocess(
    (value) => (value === undefined ? fallback : value === true || value === 'true'),
    z.boolean(),
  );
export const trackingSearchSchema = z.object({
  charging_stations: toggle(),
  points: toggle(),
  offline_couriers: toggle(),
  by_order_status: toggle(),
  in_search_orders: toggle(true),
  order_status_tab: z
    .enum(['all', 'to_origin', 'wait_order', 'to_destination', 'wait_receiver'])
    .catch('all'),
  courier_id: id.optional(),
  reassign_order_id: id.optional(),
  origin_status: z.string().optional(),
  offsetTodayOrders: z.coerce.number().int().nonnegative().catch(0),
  limitTodayOrders: z.coerce.number().int().positive().catch(5),
});
export type TrackingSearch = z.infer<typeof trackingSearchSchema>;
